import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryPaginated,
  tryUpdate,
  toQueryString,
} from "./_shared";

export type UoM = Tables<"uom">;
export type UoMInsert = Omit<TablesInsert<"uom">, "company_id">;
export type UoMUpdate = TablesUpdate<"uom">;

type UoMWithAudit = UoM & {
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
};

export interface UoMFilters {
  search?: string;
  isSI?: boolean | null;
  isImperial?: boolean | null;
  isActive?: boolean | null;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export const uomService = {
  get(id: string) {
    return tryGet<UoM>(`/api/uom/${encodeURIComponent(id)}`);
  },
  getByAbbreviation(uom: string, _companyId: string) {
    return tryGet<UoM>(`/api/uom/by-abbreviation/${encodeURIComponent(uom)}`);
  },
  list(_companyId: string) {
    return tryList<UoMWithAudit>("/api/uom");
  },
  listActive(_companyId: string) {
    return tryList<UoM>("/api/uom?variant=active");
  },
  listSI(_companyId: string) {
    return tryList<UoM>("/api/uom?variant=si");
  },
  listImperial(_companyId: string) {
    return tryList<UoM>("/api/uom?variant=imperial");
  },
  listPaginated(_companyId: string, pagination: PaginationParams, filters?: UoMFilters) {
    return tryPaginated<UoMWithAudit>(
      `/api/uom${toQueryString({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters?.search,
        isSI: filters?.isSI ?? undefined,
        isImperial: filters?.isImperial ?? undefined,
        isActive: filters?.isActive ?? undefined,
      })}`,
    );
  },
  create(data: UoMInsert, _context: ServiceContext) {
    return tryCreate<UoM>("/api/uom", data);
  },
  async createBatch(items: UoMInsert[], _context: ServiceContext) {
    const result = await tryCreate<UoM[]>("/api/uom/batch", items);
    return result;
  },
  update(id: string, data: UoMUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryUpdate<UoM>(`/api/uom/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/uom/${encodeURIComponent(id)}`);
  },
  toggleActive(id: string, isActive: boolean, _previousIsActive: boolean, _context: ServiceContext) {
    return tryUpdate<UoM>(`/api/uom/${encodeURIComponent(id)}`, { is_active: isActive });
  },
};
